import React, { useEffect, useState } from "react";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import { useToasts } from "../../../contexts/ToastContext";
import axiosClient from "../../../api/axiosClient";
import GraduationProfile from "../../../components/graduationProfile/GraduationProfile";
import GraduationTimeline from "../../../components/graduationTimeline/GraduationTimeline";
import CategoryPerformance from "../../../components/categoryPerformance/CategoryPerformance";
import RotationSummary from "./RotationSummary";
import CumulativeClinicalDaySummary from "../../../components/cumulativeClinicalDaySummary/CumulativeClinicalDaySummary";

const GraduationSummaryView = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({
    overview: {},
    timeline: [],
    competencyBasePerformance: [],
    specialityBasePerformance: [],
    instructorBasePerformance: [],
    clinicalSiteBasePerformance: [],
  });
  const { showToast } = useToasts();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axiosClient.get(`evaluations/students/graduation-summary`);
        setData(response.data);
      } catch (error) {
        showToast(error.message, 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [showToast]);

  const competencyData = data?.competencyBasePerformance?.map((item, index) => ({
    name: item.tag,
    cumulativeScore: item.cumulativeScore,
    variant: (index % 6) + 1,
  })) || [];

  const specialityData = data?.specialityBasePerformance?.map(item => ({
    name: item.speciality.name,
    cumulativeScore: item.cumulativeScore,
  })) || [];

  const instructorData = data?.instructorBasePerformance?.map(item => ({
    name: `${item.instructor.firstName} ${item.instructor.lastName}`,
    firstName: item.instructor.firstName,
    lastName: item.instructor.lastName,
    profilePicture: item.instructor.profilePicture,
    cumulativeScore: item.cumulativeScore,
  })) || [];

  const clinicalSiteData = data?.clinicalSiteBasePerformance?.map(item => ({
    name: item.clinicalSite.name,
    cumulativeScore: item.cumulativeScore,
  })) || [];

  const cumulativeViews = [
    { label: 'Competency', value: 'competency', data: competencyData, type: 'tag', itemsPerRow: 4 },
    { label: 'Specialty', value: 'specialty', data: specialityData, type: 'name', itemsPerRow: 5 },
    { label: 'Instructor', value: 'instructor', data: instructorData, type: 'instructor', itemsPerRow: 4 },
    { label: 'Clinical Site', value: 'clinicalSite', data: clinicalSiteData, type: 'name', itemsPerRow: 5 },
  ];

  const timelineData = data?.timeline?.map((item) => {
    const statusMap = {
      'COMPLETED': 'Completed',
      'IN_PROGRESS': 'In Progress',
      'NOT_STARTED': 'Not Started',
    };
    const classMap = {
      'COMPLETED': 'completed',
      'IN_PROGRESS': 'inProgress',
      'NOT_STARTED': 'notStarted',
    };

    return {
      year: item.year.year,
      status: statusMap[item.status] || 'Not Started',
      statusClass: classMap[item.status] || 'notStarted',
      cumulativeScore: item.cumulativeScore || '0',
      scorePercentage: item.cumulativeScore ? Math.round(item.cumulativeScore * 20) : 0,
      scoreColor: item.status === 'COMPLETED' ? 'green' : item.status === 'IN_PROGRESS' ? 'red' : 'grey',
    };
  }) || [];

  const { overview } = data;

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <h2>Graduation Summary</h2>
      <GraduationProfile overview={overview} />
      <GraduationTimeline data={timelineData} minimumScore={overview?.minimumScore} />
      <CategoryPerformance
        views={cumulativeViews}
        title="Cumulative Score Summary"
      />
      <RotationSummary />
      <CumulativeClinicalDaySummary />
    </>
  );
};

export default GraduationSummaryView;
